import { hasRole, type AppAbility } from "@/partner/shared/lib";
import type {
  MenuAccess,
  MenuItemProps,
  MenuSectionProps,
} from "@/partner/shared/constants";
import type { PlatformFeatureType, UserType } from "@/types/graphql/partner";

type MenuEntry = MenuItemProps | MenuSectionProps;

// `MenuSectionProps.section` — обязательная строка, `MenuItemProps.section`
// — `never` (опционален), но TS не всегда сужает union по truthy-проверке
// такого дискриминанта после early return (см. отсутствие `access` теперь
// только на MenuItemProps) — явный type guard вместо `item.section &&`.
function isSection(item: MenuEntry): item is MenuSectionProps {
  return Boolean(item.section);
}

function isAccessible(
  access: MenuAccess | undefined,
  ability: AppAbility,
  user: UserType
): boolean {
  if (!access) return true;
  if ("roles" in access) return hasRole(user, ...access.roles);
  return ability.can(access.action, access.subject);
}

/**
 * Фильтрация пунктов меню по ability (спека, «Меню и маршруты»): пункт без
 * доступа не рендерится, секция без доступных пунктов скрывается целиком.
 * Живёт в widgets/layout, а не в самом MultiLevelMenu (shared) — тому
 * нужны были бы entities (useUser/ability), которые shared импортировать
 * не может (eslint boundaries, см. комментарий про LanguageField в
 * shared/ui/index.ts). Вызывается в Layout перед передачей items.
 *
 * `liveEnabled` отсекает пункты с `requiresLive` (эфиры и точки входа):
 * на развёртывании без живых эфиров их нет в меню, а маршруты уводят на
 * дашборд (app/router.tsx). Прав это не касается: на ottkit.io набор
 * пунктов не меняется.
 *
 * `hasBillingContracts` тем же способом отсекает пункты с
 * `requiresBillingContracts` (спека §10): у организации без контрактов
 * биллинга по факту не бывает, а не наличие права на него.
 *
 * `platformFeatures` (фичефлаги текущей платформы) отсекает пункты с
 * `requiresPlatformFeature`: без PLAYER_POLLS раздела «Опросы» нет.
 */
export function filterMenuItems(
  items: MenuEntry[],
  ability: AppAbility,
  user: UserType,
  liveEnabled = true,
  hasBillingContracts = true,
  platformFeatures: readonly PlatformFeatureType[] = []
): MenuEntry[] {
  const isVisible = (item: MenuItemProps) =>
    (liveEnabled || !item.requiresLive) &&
    (hasBillingContracts || !item.requiresBillingContracts) &&
    (!item.requiresPlatformFeature ||
      platformFeatures.includes(item.requiresPlatformFeature)) &&
    isAccessible(item.access, ability, user);

  return items.reduce<MenuEntry[]>((acc, item) => {
    if (isSection(item)) {
      const filteredChildren = item.items.filter(isVisible);
      if (filteredChildren.length > 0) {
        acc.push({ ...item, items: filteredChildren });
      }
      return acc;
    }
    if (isVisible(item)) {
      acc.push(item);
    }
    return acc;
  }, []);
}

/** Первый доступный пункт меню — цель редиректа с недоступного дашборда. */
export function getFirstAccessiblePath(items: MenuEntry[]): string | undefined {
  for (const item of items) {
    if (item.section) {
      const nested = getFirstAccessiblePath(item.items);
      if (nested) return nested;
    } else if (item.to) {
      return item.to;
    }
  }
  return undefined;
}

/**
 * Пункт с адресом `path` остался в отфильтрованном меню: та же проверка
 * доступа, что и у самого пункта (счётчик обращений не запрашивается у
 * того, кто не видит «Обращения»).
 */
export function hasMenuPath(items: MenuEntry[], path: string): boolean {
  return items.some((item) =>
    isSection(item) ? hasMenuPath(item.items, path) : item.to === path
  );
}
